import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useNavConfig } from '@/hooks/useNavConfig';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { blogPostsApi } from '@/integrations/mysql/client';

const SITE_URL = 'https://brendanutricionista.com.br';
const DEFAULT_IMAGE = `${SITE_URL}/sorrindo-com-note.jpg`;

export const DEFAULT_PAGE_META: Record<string, { title: string; description: string }> = {
  '/': { title: 'Nutricionista em SP, Mogi e Suzano | Dra. Brenda Ramos', description: 'Nutricionista Dra. Brenda Ramos: atendimento clínico e comportamental em São Paulo, Mogi das Cruzes e Suzano, além de consultas online. Conheça os serviços.' },
  '/inicio': { title: 'Nutricionista em SP, Mogi e Suzano | Dra. Brenda Ramos', description: 'Nutricionista Dra. Brenda Ramos: atendimento clínico e comportamental em São Paulo, Mogi das Cruzes e Suzano, além de consultas online. Conheça os serviços.' },
  '/sobre': { title: 'Sobre a Dra. Brenda Ramos | Nutricionista', description: 'Conheça a formação, a trajetória e a abordagem clínica e comportamental da nutricionista Dra. Brenda Ramos.' },
  '/servicos': { title: 'Atendimento Nutricional e Serviços | Dra. Brenda Ramos', description: 'Conheça os atendimentos nutricionais presenciais e online da Dra. Brenda Ramos e encontre o acompanhamento adequado para seus objetivos.' },
  '/blog': { title: 'Blog de Nutrição e Alimentação | Dra. Brenda Ramos', description: 'Leia conteúdos sobre nutrição, alimentação, saúde e comportamento alimentar preparados pela nutricionista Dra. Brenda Ramos.' },
  '/imprensa': { title: 'Imprensa e Participações | Dra. Brenda Ramos', description: 'Confira entrevistas, matérias e participações da nutricionista Dra. Brenda Ramos em veículos de comunicação.' },
  '/contato': { title: 'Contato e Agendamento | Dra. Brenda Ramos', description: 'Entre em contato com a nutricionista Dra. Brenda Ramos para informações e agendamentos em São Paulo, Mogi das Cruzes, Suzano ou online.' },
  '/consultorios': { title: 'Consultórios em SP, Mogi e Suzano | Dra. Brenda Ramos', description: 'Veja endereços, horários e como chegar aos consultórios da nutricionista Dra. Brenda Ramos em São Paulo, Mogi das Cruzes e Suzano.' },
  '/avaliacoes': { title: 'Avaliação de Caso Nutricional | Dra. Brenda Ramos', description: 'Entenda como funciona a avaliação de caso nutricional e conheça o acompanhamento da Dra. Brenda Ramos.' },
  '/loja': { title: 'Loja | Dra. Brenda Ramos', description: 'Conheça os materiais e produtos disponíveis da Dra. Brenda Ramos.' },
  '/logins': { title: 'Área do Paciente | Dra. Brenda Ramos', description: 'Acesse sua área de paciente da Dra. Brenda Ramos.' },
  '/resetar-senha': { title: 'Redefinir Senha | Dra. Brenda Ramos', description: 'Redefina a senha de acesso à área do paciente.' },
  '/sitemap': { title: 'Mapa do Site | Dra. Brenda Ramos', description: 'Encontre páginas e informações do site da Dra. Brenda Ramos.' },
};

export const SEO_PAGE_LABELS: Record<string, string> = {
  '/inicio': 'Página inicial',
  '/sobre': 'Quem Somos',
  '/servicos': 'Serviços',
  '/blog': 'Blog',
  '/imprensa': 'Imprensa',
  '/contato': 'Contato',
  '/consultorios': 'Consultórios',
  '/avaliacoes': 'Avaliação de Caso',
  '/loja': 'Loja',
};

const PRIVATE_PATHS = ['/logins', '/meus-dados', '/preferencias', '/resetar-senha'];
const VISIBILITY_SETTINGS: Record<string, string> = {
  '/sobre': 'page_nossa_historia_visible',
  '/servicos': 'page_servicos_visible',
  '/blog': 'page_blog_visible',
  '/imprensa': 'page_imprensa_visible',
  '/consultorios': 'page_consultorios_visible',
  '/avaliacoes': 'page_avaliacoes_visible',
};

function setMeta(attribute: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(href: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.rel = 'canonical';
    document.head.appendChild(element);
  }
  element.href = href;
}

function safeJson(value: unknown) {
  return JSON.stringify(value).replace(/</g, '\\u003c');
}

function absoluteImageUrl(value: string | null | undefined) {
  if (!value) return DEFAULT_IMAGE;
  try {
    const url = new URL(value, SITE_URL);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : DEFAULT_IMAGE;
  } catch {
    return DEFAULT_IMAGE;
  }
}

export function SeoManager() {
  const { pathname, search } = useLocation();
  const { socialLinks } = useNavConfig();
  const { settings, isLoading } = useSiteSettings();
  const isBlogArticle = pathname.replace(/\/$/, '').startsWith('/blog/');
  const { data: blogPosts = [], isLoading: isBlogLoading } = useQuery({
    queryKey: ['published-blog-posts-seo'],
    queryFn: () => blogPostsApi.list({ published: true }),
    enabled: isBlogArticle,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const normalizedPath = pathname.replace(/\/$/, '') || '/';
    const isAdminPanel = normalizedPath === '/logins' && new URLSearchParams(search).get('admin') === 'true';
    const isConstructionMode = import.meta.env.VITE_SITE_MODE === 'construction' && !isAdminPanel;
    const articleSlug = isBlogArticle ? normalizedPath.slice('/blog/'.length) : '';
    const blogPost = isBlogArticle
      ? blogPosts.find(post => ((post as any).slug ?? post.id) === articleSlug)
      : undefined;
    if (isBlogArticle && isBlogLoading) return;
    const fallback = isBlogArticle
      ? { title: 'Artigos de Nutrição | Dra. Brenda Ramos', description: 'Conteúdos sobre nutrição, saúde e comportamento alimentar no blog da nutricionista Dra. Brenda Ramos.' }
      : { title: 'Página não encontrada | Dra. Brenda Ramos', description: 'A página solicitada não foi encontrada. Acesse os serviços, conteúdos e informações de contato da Dra. Brenda Ramos.' };
    const page = isAdminPanel
      ? { title: 'Painel Administrativo | Nutri Adm', description: 'Painel de administração do site.' }
      : isConstructionMode
      ? { title: 'Site em Construção | Dra. Brenda Ramos', description: 'Em breve, uma nova experiência para cuidar da sua saúde nutricional com ciência e empatia.' }
      : blogPost
      ? {
          title: `${blogPost.title} | Blog Dra. Brenda Ramos`,
          description: (blogPost.excerpt || blogPost.subtitle || `Leia ${blogPost.title} no blog da nutricionista Dra. Brenda Ramos.`).slice(0, 300),
        }
      : DEFAULT_PAGE_META[normalizedPath] ?? fallback;
    let pageOverrides: Record<string, { title?: string; description?: string }> = {};
    try {
      const parsed = JSON.parse(settings.seo_pages_meta || '{}');
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) pageOverrides = parsed;
    } catch { /* Ignore malformed SEO settings and use the built-in copy. */ }
    const seoPath = normalizedPath === '/' ? '/inicio' : normalizedPath;
    const pageOverride = pageOverrides[seoPath];
    if (pageOverride && !blogPost) {
      if (pageOverride.title?.trim()) page.title = pageOverride.title.trim();
      if (pageOverride.description?.trim()) page.description = pageOverride.description.trim();
    }
    const canonicalPath = normalizedPath === '/' ? '/inicio' : normalizedPath;
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;
    const isPrivate = PRIVATE_PATHS.some(path => normalizedPath === path || normalizedPath.startsWith(`${path}/`));
    const isProductionHost = ['brendanutricionista.com.br', 'www.brendanutricionista.com.br'].includes(window.location.hostname.toLowerCase());
    const visibilityKey = VISIBILITY_SETTINGS[normalizedPath]
      ?? (isBlogArticle ? VISIBILITY_SETTINGS['/blog'] : undefined);
    if (visibilityKey && isLoading) return;
    const isHidden = visibilityKey ? settings[visibilityKey] === 'false' : false;
    const isMissingBlogPost = isBlogArticle && !blogPost;
    const robots = !isProductionHost || isPrivate || isHidden || isMissingBlogPost || normalizedPath === '/sitemap' || isConstructionMode
      ? 'noindex, nofollow'
      : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
    const pageImage = absoluteImageUrl(blogPost?.featured_image_url);

    document.title = page.title;
    setMeta('name', 'description', page.description);
    setMeta('name', 'robots', robots);
    setMeta('property', 'og:title', page.title);
    setMeta('property', 'og:description', page.description);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:type', isBlogArticle ? 'article' : 'website');
    setMeta('property', 'og:image', pageImage);
    setMeta('property', 'og:image:alt', 'Dra. Brenda Ramos, nutricionista');
    setMeta('property', 'og:site_name', 'Dra. Brenda Ramos | Nutricionista');
    setMeta('property', 'og:locale', 'pt_BR');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', page.title);
    setMeta('name', 'twitter:description', page.description);
    setMeta('name', 'twitter:image', pageImage);
    setCanonical(canonicalUrl);

    const validSocialLinks = socialLinks
      .filter(link => link.enabled && /^https:\/\//i.test(link.url) && link.url !== '#')
      .map(link => link.url);
    const graph = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Person',
          '@id': `${SITE_URL}/#dra-brenda-ramos`,
          name: 'Dra. Brenda Ramos',
          jobTitle: 'Nutricionista',
          description: 'Nutricionista com atendimento clínico e comportamental, presencial e online, em São Paulo, Mogi das Cruzes e Suzano.',
          url: `${SITE_URL}/sobre`,
          image: DEFAULT_IMAGE,
          knowsAbout: ['Nutrição clínica', 'Nutrição comportamental', 'Alimentação saudável'],
          areaServed: ['São Paulo, SP', 'Mogi das Cruzes, SP', 'Suzano, SP'],
          ...(validSocialLinks.length ? { sameAs: validSocialLinks } : {}),
        },
        {
          '@type': 'WebSite',
          '@id': `${SITE_URL}/#website`,
          url: `${SITE_URL}/`,
          name: 'Dra. Brenda Ramos | Nutricionista',
          inLanguage: 'pt-BR',
          publisher: { '@id': `${SITE_URL}/#dra-brenda-ramos` },
        },
        ...(blogPost ? [{
          '@type': 'BlogPosting',
          '@id': canonicalUrl,
          headline: blogPost.title,
          description: page.description,
          image: pageImage,
          datePublished: blogPost.created_at || undefined,
          dateModified: blogPost.updated_at || blogPost.created_at || undefined,
          author: { '@id': `${SITE_URL}/#dra-brenda-ramos` },
          mainEntityOfPage: canonicalUrl,
          inLanguage: 'pt-BR',
        }] : []),
      ],
    };
    let structuredData = document.head.querySelector<HTMLScriptElement>('#site-seo-jsonld');
    if (!structuredData) {
      structuredData = document.createElement('script');
      structuredData.id = 'site-seo-jsonld';
      structuredData.type = 'application/ld+json';
      document.head.appendChild(structuredData);
    }
    structuredData.textContent = safeJson(graph);
  }, [pathname, search, settings, socialLinks, isLoading, isBlogArticle, isBlogLoading, blogPosts]);

  return null;
}
